import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getMessaging, isSupported } from 'firebase/messaging';
import type { Messaging } from 'firebase/messaging';
import { getPerformance } from 'firebase/performance';
import { getRemoteConfig, isSupported as isRemoteConfigSupported } from 'firebase/remote-config';
import type { RemoteConfig } from 'firebase/remote-config';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

export const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getDatabase(app);
export const functions = getFunctions(app);
export const perf =
  import.meta.env.PROD || import.meta.env.VITE_DB_PREFIX === 'staging' ? getPerformance(app) : null;

// Connect to Functions emulator in local dev
if (import.meta.env.DEV) {
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// Lazily initialize messaging only when supported (not available in all browsers/contexts)
let messagingInstance: Messaging | null = null;

export async function getFirebaseMessaging(): Promise<Messaging | null> {
  if (messagingInstance) return messagingInstance;

  const supported = await isSupported();
  if (!supported) return null;

  messagingInstance = getMessaging(app);
  return messagingInstance;
}

// Remote Config needs IndexedDB, so initialize it only where it's supported.
let remoteConfigInstance: Promise<RemoteConfig | null> | null = null;

export function getFirebaseRemoteConfig(): Promise<RemoteConfig | null> {
  remoteConfigInstance ??= isRemoteConfigSupported()
    .then(supported => {
      if (!supported) return null;
      const remoteConfig = getRemoteConfig(app);
      remoteConfig.settings.minimumFetchIntervalMillis = import.meta.env.DEV ? 0 : 60 * 60 * 1000;
      remoteConfig.settings.fetchTimeoutMillis = 10 * 1000;
      remoteConfig.defaultConfig = { wrap_enabled: false, xmas_enabled: false };
      return remoteConfig;
    })
    .catch(error => {
      console.error('Remote Config is unavailable:', error);
      return null;
    });
  return remoteConfigInstance;
}

export type RemoteConfigEnvironment = 'dev' | 'prod';

/**
 * Which Remote Config values to read. An empty database prefix is production and uses the template
 * defaults. Any other prefix (local, staging) is development and uses the Development condition.
 */
export function getRemoteConfigEnvironment(): RemoteConfigEnvironment {
  const prefix = import.meta.env.VITE_DB_PREFIX;
  if (prefix !== undefined) return prefix === '' ? 'prod' : 'dev';
  return import.meta.env.PROD ? 'prod' : 'dev';
}

export function getEnvDbPrefix(refString: string) {
  const prefix = import.meta.env.VITE_DB_PREFIX;
  if (prefix !== undefined) {
    return prefix ? `${prefix}_${refString}` : refString;
  }
  return import.meta.env.PROD ? refString : `local_${refString}`;
}
